import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Locator from '../../../../shared/Locator';
import PackageLabelsList from '../../../components/shared/packageLabels/PackageLabelsList';
import * as IssuesV2Store from '../../../redux/issuesV2';
import type { Project } from '../../../../shared/issuesV2';
import type { DetailsPanelListRowProps } from '../../../components/generic/DetailsPanel/DetailsPanelListRow';

import { usePackageLabelAssignments } from './index';

/**
 * Custom hook to fetch and format package label information for display in the dependency section.
 *
 * @param {string} id - The ID of the package to fetch labels for
 * @param {Project[]} projects - List of associated projects to this scope
 * @returns A DetailsPanelListRowProps object with package labels, or null if no labels exist or data is loading
 */
export const usePackageLabelsRow = (id?: string, projects?: Project[]): DetailsPanelListRowProps | null => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);

  const {
    packageId,
    revision,
  } = useMemo(() => {
    if (!id) {
      return {
        packageId: '',
        revision: undefined,
      };
    }
    const locator = new Locator(id);

    return {
      packageId: locator.toPackageString(),
      revision: locator.revision.value || undefined,
    };
  }, [id]);

  const {
    data: labelAssignments,
    isLoading: isLoadingLabelAssignments,
  } = usePackageLabelAssignments(
    id ? packageId : '',
    id ? revision : undefined,
    scope,
    true,
    true,
    true,
  );

  return useMemo(() => {
    if (!id || isLoadingLabelAssignments || !labelAssignments?.packageLabelAssignments?.length) {
      return null;
    }

    const projectIds = projects?.map((project) => project.id);

    const filteredLabelAssignments = labelAssignments.packageLabelAssignments.filter(
      (labelAssignment) => {
        // Include if no scopeId (org-level labels)
        if (!labelAssignment.scopeId) {
          return true;
        }

        // If scope is 'project', check if scopeId is in projectIds
        if (labelAssignment.scope === 'project') {
          return projectIds?.includes(labelAssignment.scopeId);
        }

        // If scope is 'revision', check if scopeId starts with any projectId
        if (labelAssignment.scope === 'revision') {
          return projectIds?.some(projectId => labelAssignment.scopeId?.startsWith(projectId));
        }

        return false;
      });

    // Return null if no labels remain after filtering
    if (!filteredLabelAssignments.length) {
      return null;
    }

    // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `packageLabelAssignment` is react-query cache data
    const labels = filteredLabelAssignments.map(
      (packageLabelAssignment) => {
        return {
          ...packageLabelAssignment,
          scope: packageLabelAssignment.scope,
          name: packageLabelAssignment.packageLabel.name,
        };
      },
    );

    return {
      label: 'Package Labels',
      content: (
        <PackageLabelsList labels={labels} dependencyLocator={new Locator(id)} labelPrefix="" enforceUnique />
      ),
    };
  }, [id, isLoadingLabelAssignments, labelAssignments, projects]);
};
